<template>
  <div
    class="container v-content-container tooltip-wrapper"
    :key="componentKey"
  >
    <div class="row tooltip-wrapper">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-cubes"></i>
          {{ $label("inventory.inventory_management", "Inventory management") }}
          <router-link
            :to="{ name: 'NewInventoryProduct' }"
            v-tooltip="{
              content: $label('inventory.new_inventory', 'New inventory'),
              classes: 'custom-tooltip'
            }"
            class="btn btn-v-lightgreen-white pull-right"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
          <select
            id="inventory-group-select"
            class="form-control header-select pull-right"
            style="margin-right: 15px;"
          >
            <option value="">{{
              $label("inventory.all_locations", "All locations")
            }}</option>
            <option value="0">{{
              $label("inventory.facility_name", "Facility name")
            }}</option>
          </select>
        </h1>
      </div>
    </div>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <alert-box
      v-show="failedRequest"
      type="warning"
      :closeable="false"
      :show-icon="false"
    >
      {{
        $label(
          "inventory.inventory_products_failed_request",
          "Request failed while loading inventory products."
        )
      }}
    </alert-box>
    <Table
      v-show="!failedRequest"
      id="inventory-products-table"
      :loading="loading"
      :columns="[
        {
          title: 'Name',
          field: 'name',
          id: row => `inventory-item-name-${row.id}`,
          sorting: true
        },
        {
          title: 'Locations',
          id: row => `inventory-item-locations-${row.id}`,
          value: row =>
            row.facilities
              .map(
                facility =>
                  `${facility.address.city} ${facility.address.post_code}`
              )
              .join(', ')
        },
        {
          title: 'Amount',
          field: 'max_available_units',
          id: row => `inventory-item-amount-${row.id}`
        },
        {
          title: 'Price',
          id: row => `inventory-item-price-${row.id}`,
          value: row =>
            row.price.form === 'gross'
              ? row.price.amount_gross
              : row.price.amount_net
        }
      ]"
      :actions="[
        {
          type: 'detail',
          onClick: row => {
            $router.push({
              name: 'InventoryProductDetails',
              params: { inventory_product_id: row.id }
            });
          }
        },
        {
          type: 'delete',
          id: row => `delete-inventory-item-button-${row.id}`,
          onClick: row => deleteInventoryProduct(row.id)
        }
      ]"
      :data="inventoryProducts"
      :meta="metaData"
      @fetchQuery="
        params => {
          fetchParams = params;
          getInventoryProducts({
            //
          });
        }
      "
    >
      <div
        id="action-dropdown"
        slot="toolbar"
        class="btn-group"
        style="margin-left: 5px;"
      >
        <button
          type="button"
          class="btn btn-primary dropdown-toggle"
          data-toggle="dropdown"
        >
          <i class="fa fa-refresh"></i> &nbsp;
          {{ $label("common.actions", "Actions") }} &nbsp;<span
            class="caret"
          ></span>
        </button>
        <ul class="dropdown-menu" role="menu">
          <li>
            <router-link :to="{ name: 'NewInventoryProduct' }"
              ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
              {{
                $label("inventory.create_new_inventory", "Create new inventory")
              }}</router-link
            >
          </li>
        </ul>
      </div>
    </Table>

    <div class="row mb-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>
  </div>
</template>

<script>
import Api from "@/api";
import Table from "@/components/Common/Table";
import AlertBox from "@/components/Common/Alerts/AlertBox";

import swal from "sweetalert";

export default {
  name: "InventoryProducts",

  components: {
    Table,
    AlertBox
  },

  data() {
    return {
      componentKey: 0,
      inventoryProducts: [],
      loading: false,
      updating: false,
      failedRequest: false,
      metaData: null,
      fetchParams: {
        page: 1,
        per_page: null,
        search: ""
      }
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      if (
        vm.$route.query.inventory_product_deleted === true ||
        vm.$route.query.inventory_product_deleted === "true"
      ) {
        swal("Success!", "The inventory product was deleted.", "success").then(
          () => {
            window.history.pushState(
              {},
              document.title,
              window.location.pathname
            );
          }
        );
      }
      vm.getInventoryProducts(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.inventory_product_deleted === true) {
      swal("Success!", "The inventory product was deleted.", "success").then(
        () => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        }
      );
    }
    this.forceRerender();
    if (
      this.fetchParams.to % this.fetchParams.per_page === 0 &&
      this.fetchParams.page !== 1
    ) {
      this.fetchParams.page -= 1;
    }
    this.getInventoryProducts(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    getInventoryProducts(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.inventoryProducts = [];
      const endpoint = this.$partnerEndpoint("inventory-products");

      Api()
        .get(endpoint, {
          params: {
            page: this.fetchParams.page,
            perPage: this.fetchParams.per_page,
            search: this.fetchParams.search
          }
        })
        .then(response => {
          this.inventoryProducts = response.data.data;
          this.metaData = response.data.meta;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteInventoryProduct(inventoryProductId) {
      if (this.loading || this.updating || inventoryProductId == null) {
        return;
      }

      swal({
        title: "Are you sure?",
        text:
          "The inventory product will be deleted and will not be able to use it",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "inventory-products/" + inventoryProductId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "InventoryProducts",
                  query: { inventory_product_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal(
                      "Success!",
                      "The inventory product was deleted.",
                      "success"
                    );
                    this.getInventoryProducts(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete inventory product.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    forceRerender() {
      this.componentKey += 1;
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style scoped>
.mt-0 {
  margin-top: 0;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.min-width-auto {
  width: 50px;
}

.pagination-select {
  font-weight: 700;
  height: 34px;
  padding-left: 10px;
  width: 70px;
  cursor: pointer;
  border: 1px solid #ddd;
  color: #666;
  outline: none;
}
</style>
